import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Stack, Typography,
} from "@mui/material";
import AdminApi, { AutomationCalendarItem, AutomationOverview, AutomationTask } from "../../api/AdminApi";

/**
 * Admin → Automatisering: het machinekamer-overzicht van Carefunded.
 * Drie banden in werkdag-volgorde: (1) statusblok (digest / settlement /
 * achterstallig), (2) de geautomatiseerde taken — compacte 14-daagse strip +
 * takenlijst met dry-run/apply-knoppen en live log, (3) het kernstuk: de
 * compliance-JAARtijdlijn (jan–dec) met alle kalenderitems op hun venster.
 * Alles klikbaar → popup met doel / stappen / data / compliance / instructie
 * / historiek, en afvinken + ongedaan maken direct vanuit de popup.
 * Registerteksten komen in het NL van de API (bron: util/taskRegistry.ts).
 */

const DAY = 24 * 3600 * 1000;

// Statuskleuren: groen OK / oranje aandacht / rood gefaald-of-gemist / blauw bezig.
const HEALTH_COLOR: Record<AutomationTask["health"], string> = {
  ok: "#2e7d32", attention: "#ed6c02", failed: "#d32f2f", missed: "#d32f2f", running: "#0288d1", manual: "#78909c",
};
const HEALTH_CHIP: Record<AutomationTask["health"], "success" | "warning" | "error" | "info" | "default"> = {
  ok: "success", attention: "warning", failed: "error", missed: "error", running: "info", manual: "default",
};
// Kalenderstatussen (incl. "herinnerd" = open + herinneringsmail al verstuurd).
const CAL_COLOR: Record<string, { bg: string; border: string }> = {
  upcoming: { bg: "var(--surface-alt)", border: "var(--border)" },
  open: { bg: "rgba(237,108,2,.16)", border: "#ed6c02" },
  reminded: { bg: "rgba(237,108,2,.30)", border: "#b25500" },
  overdue: { bg: "rgba(211,47,47,.24)", border: "#d32f2f" },
  done: { bg: "rgba(46,125,50,.16)", border: "#2e7d32" },
};
const CAL_CHIP: Record<string, "default" | "success" | "warning" | "error"> = {
  upcoming: "default", open: "warning", reminded: "warning", overdue: "error", done: "success",
};

function calDisplayState(item: AutomationCalendarItem): string {
  return item.state === "open" && item.remindedAt ? "reminded" : item.state;
}

const groupTitleSx = { fontSize: ".78rem", textTransform: "uppercase", letterSpacing: ".08em", color: "text.secondary", mb: 0.75 } as const;

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <Typography sx={{ ...groupTitleSx, mb: 1.5, mt: 0 }}>{children}</Typography>;
}

function InfoList({ items }: { items: string[] }) {
  return (
    <Stack spacing={0.4}>
      {items.map((s, i) => (
        <Typography key={i} sx={{ fontSize: ".86rem", color: "text.secondary", m: 0 }}>— {s}</Typography>
      ))}
    </Stack>
  );
}

function CodeBlock({ lines }: { lines: string[] }) {
  return (
    <Box component="pre" sx={{ m: 0, p: 1.5, fontSize: ".8rem", fontFamily: "monospace", bgcolor: "var(--surface-alt)", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", overflowX: "auto", whiteSpace: "pre-wrap" }}>
      {lines.join("\n")}
    </Box>
  );
}

// Monospace-blok met kopieer-knop (voor de kant-en-klare AI-prompts).
function CopyPromptBlock({ text }: { text: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback (oudere browsers / niet-secure context): verborgen textarea.
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } finally { document.body.removeChild(ta); }
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2500);
  };
  return (
    <Box sx={{ position: "relative" }}>
      <Box component="pre" sx={{ m: 0, p: 1.5, pr: 12, fontSize: ".78rem", fontFamily: "monospace", bgcolor: "var(--surface-alt)", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", overflowX: "auto", whiteSpace: "pre-wrap", maxHeight: 280, overflowY: "auto" }}>
        {text}
      </Box>
      <Button size="small" variant={copied ? "contained" : "outlined"} color={copied ? "success" : "primary"}
        onClick={copy} sx={{ position: "absolute", top: 8, right: 8 }}>
        {copied ? t("admin.auto.copied") : t("admin.auto.copy")}
      </Button>
    </Box>
  );
}

// Kant-en-klare AI-prompt-sectie (taak- én kalenderpopup): korte uitleg + kopieerblok.
function AiPromptSection({ title, text }: { title: string; text: string }) {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography sx={groupTitleSx}>{title}</Typography>
      <Typography sx={{ fontSize: ".82rem", color: "text.secondary", m: 0, mb: 0.75 }}>{t("admin.auto.aiPromptHint")}</Typography>
      <CopyPromptBlock text={text} />
    </Box>
  );
}

// ── (0) Introblok: "Hoe werkt deze pagina?" (inklapbaar, mensentaal) ────────

function IntroBlock() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const lines = [1, 2, 3, 4, 5, 6].map((i) => t(`admin.auto.intro${i}`));
  return (
    <Box sx={{ border: "1px solid var(--border)", borderRadius: "var(--radius-md)", bgcolor: "var(--surface)" }}>
      <Box component="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        sx={{ all: "unset", boxSizing: "border-box", display: "flex", alignItems: "center", gap: 1, width: "100%", px: 2, py: 1.25, cursor: "pointer", "&:hover": { bgcolor: "var(--surface-alt)" }, "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 } }}>
        <Box component="span" sx={{ fontSize: ".9rem" }}>ℹ️</Box>
        <Typography sx={{ fontWeight: 600, fontSize: ".92rem", m: 0, flex: 1 }}>{t("admin.auto.introTitle")}</Typography>
        <Box component="span" sx={{ fontSize: ".8rem", color: "text.secondary", transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }}>▾</Box>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 1.75 }}>
          <Stack spacing={0.75}>
            {lines.map((s, i) => (
              <Typography key={i} sx={{ fontSize: ".86rem", color: "text.secondary", m: 0 }}>— {s}</Typography>
            ))}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}

// Klikbare legenda voor de taakgezondheid (kleuren van strip + takenlijst).
const HEALTH_LEGEND_KEYS: AutomationTask["health"][] = ["ok", "running", "manual", "attention", "failed", "missed"];

function HealthLegend({ onExplain }: { onExplain: (key: string) => void }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mt: 1 }}>
      <Typography sx={{ fontSize: ".72rem", color: "text.secondary", m: 0, textTransform: "uppercase", letterSpacing: ".06em" }}>{t("admin.auto.legend")}:</Typography>
      {HEALTH_LEGEND_KEYS.map((k) => (
        <Box key={k} component="button" onClick={() => onExplain(k)} title={t("admin.auto.stateHelpTitle")}
          sx={{ all: "unset", cursor: "pointer", display: "flex", alignItems: "center", gap: 0.6, "&:hover": { textDecoration: "underline" }, "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 } }}>
          <Box component="span" sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: HEALTH_COLOR[k], flexShrink: 0 }} />
          <Typography sx={{ fontSize: ".76rem", color: "text.secondary", m: 0 }}>{t(`admin.auto.health.${k}`)}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ── Noodprompt: generieke AI-hulp als iets kapot is of onbegrijpelijk ───────

const EMERGENCY_PROMPT =
  "Ik beheer carefunded.be (repo ~/Documents/GitHub/speakhere) en heb weinig technische voorkennis. " +
  "Op de admin-pagina Automatisering staat iets op rood/achterstallig, een taak faalde, of ik begrijp niet wat ik zie.\n\n" +
  "Lees eerst speakhere-api/src/util/taskRegistry.ts — dat is de bron van waarheid: elke automatische taak en elk kalenderitem staat daar beschreven met doel, stappen, geraakte data en compliance-impact. Lees daarna docs/facturatie-explainer.md §9 (de hele jaarkalender in gewone taal). " +
  "De actuele status komt van het endpoint GET /api/admin/automation/overview; de logboeken zijn de MongoDB-collecties TaskRunLog en SettlementRunLog — ik kan je ook de logtekst of een screenshot uit de admin geven.\n\n" +
  "Leg me in gewone taal uit wat er aan de hand is, hoe erg het is en wat de veiligste volgende stap is (vaak: eerst een dry-run via admin → Automatisering). " +
  "Grenzen: geen productie-acties (kubectl, database-writes, echte geld-/mailruns) zonder mijn expliciete go, en commit/push niets zonder mijn review.\n\n" +
  "Wat ik zie: [beschrijf hier wat je ziet of plak de log]";

function EmergencyPromptBlock() {
  const { t } = useTranslation();
  return (
    <Box sx={{ border: "1px solid var(--border)", borderRadius: "var(--radius-md)", bgcolor: "var(--surface)", p: 2 }}>
      <Typography sx={{ ...groupTitleSx, mb: 0.5 }}>{t("admin.auto.emergencyTitle")}</Typography>
      <Typography sx={{ fontSize: ".84rem", color: "text.secondary", m: 0, mb: 1 }}>{t("admin.auto.emergencyText")}</Typography>
      <CopyPromptBlock text={EMERGENCY_PROMPT} />
    </Box>
  );
}

// ── (1) Statusblok ───────────────────────────────────────────────────────────

function StatusStrip({ ov }: { ov: AutomationOverview }) {
  const { t, i18n } = useTranslation();
  const digest = ov.tasks.find((x) => x.id === "status-digest");
  const settlement = ov.tasks.find((x) => x.id === "settlement");
  const overdue = ov.calendar.filter((c) => c.state === "overdue").length;
  const fmt = (at?: string | null) => (at ? new Date(at).toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" }) : t("admin.auto.statNone"));
  const cell = (label: string, task: AutomationTask | undefined) => (
    <Box sx={{ flex: "1 1 220px", p: 2, borderRadius: "var(--radius-md)", bgcolor: "var(--surface)", border: "1px solid var(--border)" }}>
      <Typography sx={{ fontSize: ".8rem", color: "text.secondary", m: 0 }}>{label}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
        {task && <Chip size="small" label={t(`admin.auto.health.${task.health}`)} color={HEALTH_CHIP[task.health]} />}
        <Typography sx={{ fontSize: ".88rem", m: 0 }}>{fmt(task?.lastRun?.at)}</Typography>
      </Box>
    </Box>
  );
  return (
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
      {cell(t("admin.auto.statDigest"), digest)}
      {cell(t("admin.auto.statSettlement"), settlement)}
      <Box sx={{ flex: "1 1 220px", p: 2, borderRadius: "var(--radius-md)", border: "1px solid", borderColor: overdue > 0 ? "#f5c6c0" : "var(--border)", bgcolor: overdue > 0 ? "#fdecea" : "var(--surface)" }}>
        <Typography sx={{ fontSize: ".8rem", color: "text.secondary", m: 0 }}>{t("admin.auto.statOverdue")}</Typography>
        <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, m: 0, mt: 0.25, color: overdue > 0 ? "error.main" : "success.main" }}>{overdue}</Typography>
      </Box>
    </Box>
  );
}

// ── (2a) Compacte cron-strip: 7 dagen terug / 7 vooruit met nu-marker ───────

function CronStrip({ ov, onSelect }: { ov: AutomationOverview; onSelect: (task: AutomationTask) => void }) {
  const { t, i18n } = useTranslation();
  const now = new Date(ov.now).getTime();
  const start = now - 7 * DAY;
  const span = 14 * DAY;
  const pct = (ts: number) => Math.min(100, Math.max(0, ((ts - start) / span) * 100));
  const cronTasks = ov.tasks.filter((x) => x.cron);
  const days = Array.from({ length: 15 }, (_, i) => start + i * DAY);
  return (
    <Box>
      <SectionTitle>{t("admin.auto.cronTitle")}</SectionTitle>
      <Box sx={{ position: "relative", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", bgcolor: "var(--surface)", overflow: "hidden" }}>
        {/* gridlijnen + nu-marker: zelfde as als de dot-tracks (start ná de labelkolom) */}
        <Box sx={{ position: "absolute", top: 0, bottom: 0, left: "150px", right: 0, pointerEvents: "none", zIndex: 0 }}>
          {days.map((d, i) => (
            <Box key={i} sx={{ position: "absolute", left: `${pct(d)}%`, top: 0, bottom: 18, width: "1px", bgcolor: "var(--border)", opacity: 0.6 }} />
          ))}
          <Box sx={{ position: "absolute", left: `${pct(now)}%`, top: 0, bottom: 0, width: "2px", bgcolor: "error.main", zIndex: 2 }} />
          <Box sx={{ position: "absolute", left: `${pct(now)}%`, bottom: 0, transform: "translateX(-50%)", fontSize: ".68rem", fontWeight: 700, color: "error.main", zIndex: 2, px: 0.5, bgcolor: "var(--surface)" }}>
            {t("admin.auto.now")}
          </Box>
        </Box>
        {cronTasks.map((task) => (
          <Box key={task.id} component="button" onClick={() => onSelect(task)}
            sx={{ all: "unset", boxSizing: "border-box", display: "flex", alignItems: "center", width: "100%", cursor: "pointer", position: "relative", height: 34, "&:hover": { bgcolor: "var(--surface-alt)" }, "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 } }}>
            <Typography sx={{ fontSize: ".78rem", fontWeight: 600, m: 0, width: 150, flexShrink: 0, pl: 1.5, boxSizing: "border-box", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", position: "relative", zIndex: 1 }}>
              {task.name.split(" (")[0]}
            </Typography>
            <Box sx={{ position: "relative", flex: 1, height: "100%" }}>
              {task.recentRuns.filter((r) => new Date(r.at).getTime() >= start).map((r, i) => (
                <Box key={`p${i}`} title={`${new Date(r.at).toLocaleString(i18n.language)}${r.dryRun ? ` (${t("admin.auto.dry")})` : ""} — ${r.ok ? "OK" : "!"}`}
                  sx={{ position: "absolute", top: "50%", left: `${pct(new Date(r.at).getTime())}%`, transform: "translate(-50%,-50%)", width: 10, height: 10, borderRadius: "50%", bgcolor: r.dryRun ? "transparent" : r.ok ? HEALTH_COLOR.ok : HEALTH_COLOR.failed, border: "2px solid", borderColor: r.ok ? HEALTH_COLOR.ok : HEALTH_COLOR.failed }} />
              ))}
              {task.upcoming.map((u, i) => (
                <Box key={`u${i}`} title={new Date(u).toLocaleString(i18n.language)}
                  sx={{ position: "absolute", top: "50%", left: `${pct(new Date(u).getTime())}%`, transform: "translate(-50%,-50%)", width: 9, height: 9, borderRadius: "50%", bgcolor: "transparent", border: "2px dashed", borderColor: "text.secondary", opacity: 0.7 }} />
              ))}
              {task.running && (
                <Box sx={{ position: "absolute", top: "50%", left: `${pct(now)}%`, transform: "translate(-50%,-50%)", width: 12, height: 12, borderRadius: "50%", bgcolor: HEALTH_COLOR.running, boxShadow: "0 0 0 4px rgba(2,136,209,.25)" }} />
              )}
            </Box>
          </Box>
        ))}
        {/* dag-labels */}
        <Box sx={{ position: "relative", height: 18, ml: "150px" }}>
          {days.filter((_, i) => i % 2 === 0).map((d, i) => (
            <Typography key={i} sx={{ position: "absolute", left: `${((d - start) / span) * 100}%`, transform: "translateX(-50%)", fontSize: ".66rem", color: "text.secondary", m: 0 }}>
              {new Date(d).toLocaleDateString(i18n.language, { day: "numeric", month: "numeric" })}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ── (2b) Takenlijst met run-knoppen + live log ──────────────────────────────

interface LiveRun { runId: string; name: string }

function TaskList({ ov, onSelect, onRun, live, busyJob }: {
  ov: AutomationOverview;
  onSelect: (task: AutomationTask) => void;
  onRun: (task: AutomationTask, kind: "dry" | "apply") => void;
  live: LiveRun | null;
  busyJob: string | null;
}) {
  const { t, i18n } = useTranslation();
  const fmt = (iso?: string | null) => (iso ? new Date(iso).toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" }) : "—");
  return (
    <Box>
      <SectionTitle>{t("admin.auto.tasksTitle")}</SectionTitle>
      <Stack divider={<Divider />} spacing={0}>
        {ov.tasks.map((task) => (
          <Box key={task.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25, flexWrap: "wrap" }}>
            <Box component="span" sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: HEALTH_COLOR[task.health], flexShrink: 0 }} />
            <Box component="button" onClick={() => onSelect(task)}
              sx={{ all: "unset", cursor: "pointer", flex: "1 1 260px", minWidth: 220, fontFamily: "inherit", "&:hover .autoTaskName": { textDecoration: "underline" }, "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 } }}>
              <Typography className="autoTaskName" sx={{ fontWeight: 600, fontSize: ".92rem", m: 0 }}>{task.name}</Typography>
              <Typography sx={{ fontSize: ".8rem", color: "text.secondary", m: 0 }}>
                {task.cron ? task.cron.textNl : t("admin.auto.manualNoCron")}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 190 }}>
              <Typography sx={{ fontSize: ".78rem", color: "text.secondary", m: 0 }}>
                {t("admin.auto.lastRun")}: {task.lastRun ? fmt(task.lastRun.at) : t("admin.auto.never")}
                {task.lastRun && <Box component="span" sx={{ color: task.lastRun.ok ? "success.main" : "error.main", fontWeight: 700 }}> · {task.lastRun.ok ? "OK" : "!"}</Box>}
              </Typography>
              {task.cron && <Typography sx={{ fontSize: ".78rem", color: "text.secondary", m: 0 }}>{t("admin.auto.nextRun")}: {fmt(task.nextRun)}</Typography>}
            </Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              {task.running ? (
                <Chip size="small" color="info" label={t("admin.auto.health.running")} />
              ) : (
                <>
                  {task.adminJobs?.dry && (
                    <Button size="small" variant="outlined" disabled={busyJob !== null || live !== null} onClick={() => onRun(task, "dry")}>
                      {t("admin.auto.dryRun")}
                    </Button>
                  )}
                  {task.adminJobs?.apply && (
                    <Button size="small" variant={task.applyConfirmTextNl ? "contained" : "outlined"} color={task.applyConfirmTextNl ? "warning" : "primary"}
                      disabled={busyJob !== null || live !== null} onClick={() => onRun(task, "apply")}>
                      {t("admin.auto.runNow")}
                    </Button>
                  )}
                </>
              )}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function LiveLog({ live, log, done, onClose }: { live: LiveRun; log: string; done: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const preRef = useRef<HTMLPreElement>(null);
  useEffect(() => {
    if (preRef.current) preRef.current.scrollTop = preRef.current.scrollHeight;
  }, [log]);
  return (
    <Box sx={{ border: "1px solid var(--border)", borderRadius: "var(--radius-md)", overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, bgcolor: "var(--surface-alt)" }}>
        {!done && <CircularProgress size={14} />}
        <Typography sx={{ fontSize: ".85rem", fontWeight: 600, m: 0, flex: 1 }}>
          {t("admin.auto.logTitle", { name: live.name })}{done ? ` — ${t("admin.auto.logDone")}` : ""}
        </Typography>
        <Button size="small" onClick={onClose}>{t("admin.auto.close")}</Button>
      </Box>
      <Box component="pre" ref={preRef} sx={{ m: 0, p: 1.5, height: 260, overflow: "auto", fontSize: ".76rem", fontFamily: "monospace", bgcolor: "#1c2128", color: "#d3dae3", whiteSpace: "pre-wrap" }}>
        {log || "…"}
      </Box>
    </Box>
  );
}

// ── (3) Het kernstuk: de compliance-JAARtijdlijn (jan–dec) ──────────────────

function dayOfYearPct(d: Date, year: number): number {
  const start = new Date(year, 0, 1).getTime();
  const span = new Date(year + 1, 0, 1).getTime() - start;
  return Math.min(100, Math.max(0, ((d.getTime() - start) / span) * 100));
}

function YearCalendar({ ov, onSelect, onExplain }: { ov: AutomationOverview; onSelect: (item: AutomationCalendarItem) => void; onExplain: (key: string) => void }) {
  const { t, i18n } = useTranslation();
  const now = new Date(ov.now);
  const year = now.getFullYear();
  const nowPct = dayOfYearPct(now, year);
  const months = Array.from({ length: 12 }, (_, m) => new Date(year, m, 1));
  const items = useMemo(
    () => [...ov.calendar].sort((a, b) => (a.windowStart < b.windowStart ? -1 : a.windowStart > b.windowStart ? 1 : a.deadline < b.deadline ? -1 : 1)),
    [ov.calendar]
  );
  const LABEL_W = { xs: 130, sm: 210 };
  const legend: { key: string; state: string }[] = [
    { key: "upcoming", state: "upcoming" }, { key: "open", state: "open" }, { key: "reminded", state: "reminded" },
    { key: "done", state: "done" }, { key: "overdue", state: "overdue" },
  ];
  return (
    <Box>
      <SectionTitle>{t("admin.auto.calTitle", { year })}</SectionTitle>
      <Box sx={{ position: "relative", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", bgcolor: "var(--surface)", overflow: "hidden" }}>
        {/* maandheader */}
        <Box sx={{ display: "flex", ml: { xs: `${LABEL_W.xs}px`, sm: `${LABEL_W.sm}px` }, borderBottom: "1px solid var(--border)" }}>
          {months.map((m, i) => (
            <Typography key={i} sx={{ flex: 1, fontSize: ".68rem", textAlign: "center", color: "text.secondary", py: 0.5, m: 0, textTransform: "uppercase", letterSpacing: ".04em", borderLeft: i === 0 ? "none" : "1px solid var(--border)" }}>
              {m.toLocaleDateString(i18n.language, { month: "short" })}
            </Typography>
          ))}
        </Box>
        <Box sx={{ position: "relative" }}>
          {/* maand-gridlijnen over alle rijen */}
          <Box sx={{ position: "absolute", top: 0, bottom: 0, left: { xs: `${LABEL_W.xs}px`, sm: `${LABEL_W.sm}px` }, right: 0, pointerEvents: "none" }}>
            {months.slice(1).map((m, i) => (
              <Box key={i} sx={{ position: "absolute", left: `${dayOfYearPct(m, year)}%`, top: 0, bottom: 0, width: "1px", bgcolor: "var(--border)", opacity: 0.7 }} />
            ))}
            {/* nu-marker */}
            <Box sx={{ position: "absolute", left: `${nowPct}%`, top: 0, bottom: 0, width: "2px", bgcolor: "error.main", zIndex: 2 }} />
          </Box>
          {items.map((item) => {
            const ds = calDisplayState(item);
            const c = CAL_COLOR[ds];
            const isOneShot = item.cadence !== "yearly"; // once + monthly_until_done: eenmalige acties
            const ws = new Date(item.windowStart + "T00:00:00");
            const dl = new Date(item.deadline + "T23:59:59");
            const left = dayOfYearPct(ws, year);
            const width = Math.max(1.6, dayOfYearPct(dl, year) - left);
            return (
              <Box key={item.id} component="button" onClick={() => onSelect(item)}
                sx={{ all: "unset", boxSizing: "border-box", display: "flex", alignItems: "center", width: "100%", cursor: "pointer", height: 34, "&:hover": { bgcolor: "var(--surface-alt)" }, "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 }, borderBottom: "1px solid var(--border)", "&:last-of-type": { borderBottom: "none" } }}>
                <Box sx={{ width: { xs: `${LABEL_W.xs}px`, sm: `${LABEL_W.sm}px` }, flexShrink: 0, px: 1.25, display: "flex", alignItems: "center", gap: 0.5, overflow: "hidden" }}>
                  {item.state === "overdue" && <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "error.main", flexShrink: 0 }} />}
                  <Typography sx={{ fontSize: ".78rem", fontWeight: item.state === "overdue" ? 700 : 500, color: item.state === "overdue" ? "error.main" : "text.primary", m: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {item.title}
                  </Typography>
                </Box>
                <Box sx={{ position: "relative", flex: 1, height: "100%" }}>
                  <Box sx={{
                    position: "absolute", top: "50%", transform: "translateY(-50%)",
                    left: `${left}%`, width: `${width}%`, height: 16,
                    bgcolor: c.bg, borderRadius: isOneShot ? "3px" : "8px",
                    border: `${item.state === "overdue" ? 2 : 1.5}px ${isOneShot ? "dashed" : "solid"} ${c.border}`,
                    display: "flex", alignItems: "center", justifyContent: "flex-end", pr: 0.25,
                  }}>
                    {item.cadence === "monthly_until_done" && item.state !== "done" && (
                      <Box component="span" sx={{ fontSize: ".62rem", lineHeight: 1, color: c.border, pr: 0.25 }}>↻</Box>
                    )}
                    {ds === "reminded" && <Box component="span" sx={{ fontSize: ".62rem", lineHeight: 1, color: c.border }}>✉</Box>}
                    {ds === "done" && <Box component="span" sx={{ fontSize: ".66rem", lineHeight: 1, color: c.border, fontWeight: 700 }}>✓</Box>}
                  </Box>
                  {/* deadline-markering: ruit voor eenmalige acties, streep voor jaarlijkse */}
                  <Box sx={{
                    position: "absolute", top: "50%", left: `${Math.min(99.6, left + width)}%`,
                    transform: isOneShot ? "translate(-50%,-50%) rotate(45deg)" : "translate(-50%,-50%)",
                    width: isOneShot ? 8 : 3, height: isOneShot ? 8 : 22,
                    bgcolor: c.border, borderRadius: isOneShot ? 0 : "2px",
                  }} />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      {/* legenda: statussen + vormtaal */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mt: 1 }}>
        <Typography sx={{ fontSize: ".72rem", color: "text.secondary", m: 0, textTransform: "uppercase", letterSpacing: ".06em" }}>{t("admin.auto.legend")}:</Typography>
        {legend.map((l) => (
          <Box key={l.key} component="button" onClick={() => onExplain(l.key)} title={t("admin.auto.stateHelpTitle")}
            sx={{ all: "unset", cursor: "pointer", display: "flex", alignItems: "center", gap: 0.6, "&:hover": { textDecoration: "underline" }, "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 } }}>
            <Box component="span" sx={{ width: 14, height: 9, borderRadius: "4px", bgcolor: CAL_COLOR[l.state].bg, border: `1.5px solid ${CAL_COLOR[l.state].border}` }} />
            <Typography sx={{ fontSize: ".76rem", color: "text.secondary", m: 0 }}>{t(`admin.auto.state.${l.key}`)}</Typography>
          </Box>
        ))}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Box component="span" sx={{ width: 14, height: 9, borderRadius: "2px", border: "1.5px dashed var(--border)" }} />
          <Typography sx={{ fontSize: ".76rem", color: "text.secondary", m: 0 }}>{t("admin.auto.onceHint")}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Typography sx={{ fontSize: ".8rem", color: "text.secondary", m: 0 }}>↻</Typography>
          <Typography sx={{ fontSize: ".76rem", color: "text.secondary", m: 0 }}>{t("admin.auto.monthlyHint")}</Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ── Popups ──────────────────────────────────────────────────────────────────

function TaskDialog({ task, onClose, onRun, onOpenCalendarItem, calendar }: {
  task: AutomationTask;
  onClose: () => void;
  onRun: (task: AutomationTask, kind: "dry" | "apply") => void;
  onOpenCalendarItem: (item: AutomationCalendarItem) => void;
  calendar: AutomationCalendarItem[];
}) {
  const { t, i18n } = useTranslation();
  const fmt = (iso?: string | null) => (iso ? new Date(iso).toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" }) : "—");
  const linked = task.calendarItemId ? calendar.find((c) => c.id === task.calendarItemId) : undefined;
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Box component="span">{task.name}</Box>
          <Chip size="small" label={t(`admin.auto.health.${task.health}`)} color={HEALTH_CHIP[task.health]} />
        </Box>
        <Typography sx={{ fontSize: ".85rem", color: "text.secondary", m: 0, mt: 0.5, fontWeight: 400 }}>
          {task.cron ? `${task.cron.textNl} · cron ${task.cron.expression} (${task.cron.yaml})` : t("admin.auto.manualNoCron")}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Box>
            <Typography sx={groupTitleSx}>{t("admin.auto.purpose")}</Typography>
            <Typography sx={{ fontSize: ".9rem", m: 0 }}>{task.purpose}</Typography>
          </Box>
          <Box>
            <Typography sx={groupTitleSx}>{t("admin.auto.steps")}</Typography>
            <Stack component="ol" spacing={0.4} sx={{ m: 0, pl: 2.5 }}>
              {task.steps.map((s, i) => (
                <Typography key={i} component="li" sx={{ fontSize: ".86rem", color: "text.secondary" }}>{s}</Typography>
              ))}
            </Stack>
          </Box>
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <Box sx={{ flex: "1 1 260px" }}>
              <Typography sx={groupTitleSx}>{t("admin.auto.reads")}</Typography>
              <InfoList items={task.reads} />
            </Box>
            <Box sx={{ flex: "1 1 260px" }}>
              <Typography sx={groupTitleSx}>{t("admin.auto.writes")}</Typography>
              <InfoList items={task.writes} />
            </Box>
          </Box>
          <Box>
            <Typography sx={groupTitleSx}>{t("admin.auto.deps")}</Typography>
            {task.externalDeps.length ? <InfoList items={task.externalDeps} /> : <Typography sx={{ fontSize: ".86rem", color: "text.secondary", m: 0 }}>{t("admin.auto.depsNone")}</Typography>}
          </Box>
          <Box>
            <Typography sx={groupTitleSx}>{t("admin.auto.complianceImpact")}</Typography>
            <InfoList items={task.compliance} />
          </Box>
          <Box>
            <Typography sx={groupTitleSx}>{t("admin.auto.schedule")}</Typography>
            <Typography sx={{ fontSize: ".86rem", color: "text.secondary", m: 0 }}>
              {t("admin.auto.lastRun")}: {task.lastRun ? `${fmt(task.lastRun.at)} — ${task.lastRun.ok ? "OK" : "!"}` : t("admin.auto.never")}
            </Typography>
            {task.cron && <Typography sx={{ fontSize: ".86rem", color: "text.secondary", m: 0 }}>{t("admin.auto.nextRun")}: {fmt(task.nextRun)}</Typography>}
            {task.lastRun && !task.lastRun.ok && task.lastRun.errors && task.lastRun.errors.length > 0 && (
              <Alert severity="error" sx={{ mt: 1 }}>{t("admin.auto.errors")}: {task.lastRun.errors.join(" · ")}</Alert>
            )}
          </Box>
          {task.manualInstruction && (
            <Box>
              <Typography sx={groupTitleSx}>{t("admin.auto.instruction")}</Typography>
              <CodeBlock lines={[task.manualInstruction]} />
            </Box>
          )}
          {task.aiPrompt && <AiPromptSection title={t("admin.auto.aiPromptTask")} text={task.aiPrompt} />}
          {linked && (
            <Box>
              <Typography sx={groupTitleSx}>{t("admin.auto.calendarLink")}</Typography>
              <Chip size="small" variant="outlined" color={CAL_CHIP[calDisplayState(linked)]} label={linked.title}
                onClick={() => { onClose(); onOpenCalendarItem(linked); }} />
            </Box>
          )}
          <Box>
            <Typography sx={groupTitleSx}>{t("admin.auto.source")}</Typography>
            <Stack spacing={0.25}>
              {task.sourceRefs.map((s, i) => (
                <Typography key={i} sx={{ fontSize: ".8rem", fontFamily: "monospace", color: "text.secondary", m: 0 }}>{s}</Typography>
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        {!task.running && task.adminJobs?.dry && (
          <Button size="small" variant="outlined" onClick={() => { onClose(); onRun(task, "dry"); }}>{t("admin.auto.dryRun")}</Button>
        )}
        {!task.running && task.adminJobs?.apply && (
          <Button size="small" variant="outlined" color="warning" onClick={() => { onClose(); onRun(task, "apply"); }}>{t("admin.auto.runNow")}</Button>
        )}
        <Button size="small" onClick={onClose}>{t("admin.auto.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}

function CalendarDialog({ item, onClose, onChanged, onOpenTask, tasks }: {
  item: AutomationCalendarItem;
  onClose: () => void;
  onChanged: () => void;
  onOpenTask: (task: AutomationTask) => void;
  tasks: AutomationTask[];
}) {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fmtD = (iso?: string) => (iso ? new Date(iso).toLocaleDateString(i18n.language, { day: "numeric", month: "long", year: "numeric" }) : "—");
  const ds = calDisplayState(item);
  const linkedTask = item.meta?.taskId ? tasks.find((x) => x.id === item.meta!.taskId) : undefined;
  const act = async (fn: () => Promise<void>) => {
    setErr(""); setBusy(true);
    try { await fn(); onChanged(); onClose(); }
    catch { setErr(t("admin.auto.startError")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Box component="span">{item.title}</Box>
          <Chip size="small" label={t(`admin.auto.state.${ds}`)} color={CAL_CHIP[ds]} />
          <Chip size="small" variant="outlined" label={t(`admin.auto.cadence.${item.cadence}`)} />
        </Box>
        <Typography sx={{ fontSize: ".85rem", color: "text.secondary", m: 0, mt: 0.5, fontWeight: 400 }}>
          {t("admin.auto.cycle", { cycle: item.cycle })} · {t("admin.auto.window")}: {fmtD(item.windowStart)} → <Box component="strong" sx={{ color: item.state === "overdue" ? "error.main" : "inherit" }}>{t("admin.auto.deadline").toLowerCase()} {fmtD(item.deadline)}</Box>
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Typography sx={{ fontSize: ".9rem", m: 0 }}>{item.description}</Typography>
          {item.meta && item.meta.achtergrond && item.meta.achtergrond.length > 0 && (
            <Box>
              <Typography sx={groupTitleSx}>{t("admin.auto.background")}</Typography>
              <InfoList items={item.meta.achtergrond} />
            </Box>
          )}
          {item.meta && (
            <Box>
              <Typography sx={groupTitleSx}>{t("admin.auto.instruction")}</Typography>
              <Typography sx={{ fontSize: ".88rem", color: "text.secondary", m: 0 }}>{item.meta.instruction}</Typography>
              {item.meta.commands && item.meta.commands.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography sx={{ ...groupTitleSx, mb: 0.5 }}>{t("admin.auto.commands")}</Typography>
                  <CodeBlock lines={item.meta.commands} />
                </Box>
              )}
              {item.meta.links && item.meta.links.length > 0 && (
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1 }}>
                  {item.meta.links.map((l, i) => (
                    <Box key={i} component="a" href={l.url} target="_blank" rel="noreferrer" sx={{ fontSize: ".85rem", color: "var(--link)" }}>{l.label} ↗</Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
          {item.meta?.aiPrompt && <AiPromptSection title={t("admin.auto.aiPrompt")} text={item.meta.aiPrompt} />}
          {linkedTask && (
            <Box>
              <Typography sx={groupTitleSx}>{t("admin.auto.taskLink")}</Typography>
              <Chip size="small" variant="outlined" label={linkedTask.name} onClick={() => { onClose(); onOpenTask(linkedTask); }} />
            </Box>
          )}
          <Box>
            <Typography sx={groupTitleSx}>{t("admin.auto.history")}</Typography>
            {item.history.length === 0 ? (
              <Typography sx={{ fontSize: ".86rem", color: "text.secondary", m: 0 }}>{t("admin.auto.historyNone")}</Typography>
            ) : (
              <Stack spacing={0.4}>
                {item.history.map((h, i) => (
                  <Typography key={i} sx={{ fontSize: ".84rem", color: "text.secondary", m: 0 }}>
                    <Box component="span" sx={{ fontFamily: "monospace" }}>{h.cycle}</Box>
                    {h.remindedAt ? ` — ${t("admin.auto.remindedOn", { date: fmtD(h.remindedAt) })}` : ""}
                    {h.doneAt ? ` — ${t("admin.auto.doneOn", { date: fmtD(h.doneAt), who: h.doneBy || "admin" })}` : ""}
                  </Typography>
                ))}
              </Stack>
            )}
          </Box>
          {item.meta && item.meta.sourceRefs.length > 0 && (
            <Box>
              <Typography sx={groupTitleSx}>{t("admin.auto.source")}</Typography>
              <Stack spacing={0.25}>
                {item.meta.sourceRefs.map((s, i) => (
                  <Typography key={i} sx={{ fontSize: ".8rem", fontFamily: "monospace", color: "text.secondary", m: 0 }}>{s}</Typography>
                ))}
              </Stack>
            </Box>
          )}
          {err && <Alert severity="error" onClose={() => setErr("")}>{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        {item.state !== "done" ? (
          <Button size="small" variant="contained" color="success" disabled={busy} onClick={() => act(() => AdminApi.markComplianceDone(item.id))}>
            {t("admin.auto.markDone")}
          </Button>
        ) : (
          <Button size="small" variant="outlined" color="warning" disabled={busy} onClick={() => act(() => AdminApi.markComplianceUndone(item.id))}>
            {t("admin.auto.undoDone")}
          </Button>
        )}
        <Button size="small" onClick={onClose}>{t("admin.auto.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Hoofdcomponent ──────────────────────────────────────────────────────────

export default function AdminAutomation() {
  const { t } = useTranslation();
  const [ov, setOv] = useState<AutomationOverview | null>(null);
  const [loadErr, setLoadErr] = useState(false);
  const [runErr, setRunErr] = useState("");
  const [selTask, setSelTask] = useState<AutomationTask | null>(null);
  const [selCal, setSelCal] = useState<AutomationCalendarItem | null>(null);
  const [confirm, setConfirm] = useState<{ job: string; name: string; text: string; writes: string[]; hasDry: boolean } | null>(null);
  const [explain, setExplain] = useState<{ label: string; text: string } | null>(null);
  const [busyJob, setBusyJob] = useState<string | null>(null);
  const [live, setLive] = useState<LiveRun | null>(null);
  const [log, setLog] = useState("");
  const [liveDone, setLiveDone] = useState(false);
  const offsetRef = useRef(0);

  const load = useCallback(() => {
    AdminApi.automationOverview().then((r) => { setOv(r); setLoadErr(false); }).catch(() => setLoadErr(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Live log-tail voor een zojuist gestarte run; één extra poll na afloop
  // (de exit-footer kan net na de laatste tail geschreven zijn), dan overview
  // verversen zodat status/gezondheid de nieuwe run tonen.
  useEffect(() => {
    if (!live) return;
    let cancelled = false;
    let finishing = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      try {
        const r = await AdminApi.automationRunLog(live.runId, offsetRef.current);
        if (cancelled) return;
        if (r.content) { setLog((p) => p + r.content); offsetRef.current = r.nextOffset; }
        if (r.exists && !r.running) {
          if (finishing) { setLiveDone(true); load(); return; }
          finishing = true;
        }
      } catch { /* tijdelijk — blijf pollen */ }
      if (!cancelled) timer = setTimeout(tick, 1500);
    };
    timer = setTimeout(tick, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [live, load]);

  const startJob = async (job: string, name: string, withConfirm?: boolean) => {
    setRunErr(""); setBusyJob(job);
    try {
      const { runId } = await AdminApi.automationRun(job, withConfirm);
      offsetRef.current = 0; setLog(""); setLiveDone(false);
      setLive({ runId, name });
      load();
    } catch (e: any) {
      setRunErr(e?.response?.status === 409 ? t("admin.auto.alreadyRunning") : e?.response?.data?.message || t("admin.auto.startError"));
    } finally {
      setBusyJob(null);
    }
  };

  const onRun = (task: AutomationTask, kind: "dry" | "apply") => {
    const job = kind === "dry" ? task.adminJobs?.dry : task.adminJobs?.apply;
    if (!job) return;
    // Geld-/mailruns: eerst de bevestigingsdialoog (server eist confirm: true).
    if (kind === "apply" && task.applyConfirmTextNl) {
      setConfirm({ job, name: task.name, text: task.applyConfirmTextNl, writes: task.writes, hasDry: !!task.adminJobs?.dry });
    } else startJob(job, task.name);
  };

  // Klikbare kleur-uitleg (legenda's): wat betekent deze status en wat doe ik dan?
  const explainHealth = (key: string) => setExplain({ label: t(`admin.auto.health.${key}`), text: t(`admin.auto.healthHelp.${key}`) });
  const explainCalState = (key: string) => setExplain({ label: t(`admin.auto.state.${key}`), text: t(`admin.auto.stateHelp.${key}`) });

  if (loadErr) return <Alert severity="error">{t("admin.auto.loadError")}</Alert>;
  if (!ov) return <CircularProgress size={24} sx={{ color: "primary.main" }} />;

  return (
    <Stack spacing={3.5}>
      <IntroBlock />
      <StatusStrip ov={ov} />
      {runErr && <Alert severity="error" onClose={() => setRunErr("")}>{runErr}</Alert>}
      <Box>
        <CronStrip ov={ov} onSelect={setSelTask} />
        <HealthLegend onExplain={explainHealth} />
      </Box>
      <TaskList ov={ov} onSelect={setSelTask} onRun={onRun} live={live && !liveDone ? live : null} busyJob={busyJob} />
      {live && <LiveLog live={live} log={log} done={liveDone} onClose={() => setLive(null)} />}
      <YearCalendar ov={ov} onSelect={setSelCal} onExplain={explainCalState} />
      <EmergencyPromptBlock />

      {selTask && (
        <TaskDialog task={selTask} onClose={() => setSelTask(null)} onRun={onRun}
          onOpenCalendarItem={setSelCal} calendar={ov.calendar} />
      )}
      {selCal && (
        <CalendarDialog item={selCal} onClose={() => setSelCal(null)} onChanged={load}
          onOpenTask={setSelTask} tasks={ov.tasks} />
      )}
      {explain && (
        <Dialog open onClose={() => setExplain(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{t("admin.auto.stateHelpTitle")} — {explain.label}</DialogTitle>
          <DialogContent dividers>
            <Typography sx={{ fontSize: ".9rem", m: 0 }}>{explain.text}</Typography>
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={() => setExplain(null)}>{t("admin.auto.close")}</Button>
          </DialogActions>
        </Dialog>
      )}
      {confirm && (
        <Dialog open onClose={() => setConfirm(null)} maxWidth="sm" fullWidth>
          <DialogTitle>{t("admin.auto.confirmTitle")} — {confirm.name}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.5}>
              <Typography sx={{ fontSize: ".92rem", m: 0 }}>{confirm.text}</Typography>
              {confirm.writes.length > 0 && (
                <Box>
                  <Typography sx={groupTitleSx}>{t("admin.auto.confirmWrites")}</Typography>
                  <InfoList items={confirm.writes} />
                </Box>
              )}
              {confirm.hasDry && <Alert severity="info">{t("admin.auto.confirmDryHint")}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={() => setConfirm(null)}>{t("admin.auto.cancel")}</Button>
            <Button size="small" variant="contained" color="warning" disabled={busyJob !== null}
              onClick={() => { const c = confirm; setConfirm(null); startJob(c.job, c.name, true); }}>
              {t("admin.auto.confirmRun")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
}
